import {
  getFailureCode,
  getNumericCode,
  hasContent,
  isObject,
  KeyValue,
  toIdentifier,
  toSafeReason
} from './utils';
import { getKnownFailureClassification } from './classification';

const FAILURE_TRANSACTION_STATUSES = new Set([
  'ERROR',
  'FAILED',
  'FAILURE',
  'INVALIDATED',
  'REJECTED',
  'UNSUCCESSFUL'
]);

const RESULT_FAILURE_PATTERN = /(^|_)(error|fail|failed|failure|invalid|rejected|denied|not_applied|already_applied|unsuccessful)($|_)/i;

const getResultFailure = (value: unknown): string | undefined => {
  if (!isObject(value) || typeof value.result !== 'string') return undefined;
  if (!RESULT_FAILURE_PATTERN.test(value.result.replace(/-/g, '_'))) {
    return undefined;
  }
  return toSafeReason(value.result, 'resultFailure');
};

const findKnownResultFailure = (response: KeyValue): string | undefined => {
  const body = isObject(response.body) ? response.body : undefined;
  const directContainers = [response, body].filter(isObject);
  const wrappedContainers = directContainers.flatMap(container =>
    Object.values(container)
      .filter(isObject)
      .slice(0, 25)
  );

  for (const container of [...directContainers, ...wrappedContainers]) {
    const failure = getResultFailure(container);
    if (failure) return failure;
  }
  return undefined;
};

const getErrorContainerReason = (
  value: unknown,
  fallback: string
): string | undefined => {
  if (!hasContent(value)) return undefined;
  if (!isObject(value)) return fallback;

  const errorCode = value.code || value.errorCode;
  const numericErrorCode = getNumericCode(errorCode);
  if (numericErrorCode && numericErrorCode >= 400) {
    return `responseCode${numericErrorCode}`;
  }
  return errorCode ? toSafeReason(errorCode, fallback) : fallback;
};

const getExplicitFailureReason = (value: unknown): string | undefined => {
  if (!isObject(value)) return undefined;

  const errorInfoReason = getErrorContainerReason(
    value.ErrorInfo || value.errorInfo,
    'responseErrorInfo'
  );
  if (errorInfoReason) return errorInfoReason;

  const errorReason = getErrorContainerReason(value.error, 'responseError');
  if (errorReason) return errorReason;

  if (hasContent(value.fault)) {
    return toSafeReason(value.fault, 'gatewayFault');
  }
  if (value.success === false || value.isSuccess === false) {
    return toSafeReason(value.nextAction, 'responseUnsuccessful');
  }
  return hasContent(value.errors) ? 'responseErrors' : undefined;
};

export const getApiResponseFailureReason = (
  responseJson: unknown
): string | undefined => {
  if (!isObject(responseJson)) return undefined;

  const metadata = isObject(responseJson.metadata)
    ? responseJson.metadata
    : undefined;
  if (hasContent(responseJson.fault)) {
    return toSafeReason(responseJson.fault, 'gatewayFault');
  }

  const knownFailure = getKnownFailureClassification(responseJson);
  if (knownFailure) return knownFailure.reason;

  const metadataCode = getFailureCode(metadata?.code, metadata?.status);
  if (metadataCode) return `responseCode${metadataCode}`;

  const responseCode = getFailureCode(
    responseJson.statusCode,
    responseJson.code,
    responseJson.status
  );
  if (responseCode) return `responseCode${responseCode}`;

  const rootFailure = getExplicitFailureReason(responseJson);
  if (rootFailure) return rootFailure;

  const transactionStatus =
    typeof metadata?.transactionStatus === 'string'
      ? metadata.transactionStatus.toUpperCase()
      : undefined;
  if (
    transactionStatus &&
    FAILURE_TRANSACTION_STATUSES.has(transactionStatus)
  ) {
    const statusReason = toIdentifier(transactionStatus);
    return `transaction${statusReason
      .charAt(0)
      .toUpperCase()}${statusReason.slice(1)}`;
  }

  const bodyFailure = getExplicitFailureReason(responseJson.body);
  if (bodyFailure) return bodyFailure;

  return findKnownResultFailure(responseJson);
};
